import { create } from 'zustand';
import type { Annotation } from '@hump/shared-types';
import { annotationRepository } from '../lib/repository';
import { knowledgeRepository } from '../lib/repository/knowledge';
import { useReaderStore } from './reader';

interface AnnotationState {
  editingBlockId: string | null;
  docId: string | null;
  error: string | null;
  /** 当前 block 的已有批注列表（8.6 编辑入口） */
  existingAnnotations: Annotation[];
  /** 8.2 划词批注：打开编辑器时传入的初始内容（选中文本） */
  initialText: string;

  openEditor: (docId: string, blockId: string, initialText?: string) => Promise<void>;
  closeEditor: () => void;
  saveAnnotation: (text: string) => Promise<void>;
  updateAnnotation: (annotationId: string, text: string) => Promise<void>;
  removeAnnotation: (annotationId: string) => Promise<void>;
}

export const useAnnotationStore = create<AnnotationState>((set, get) => ({
  editingBlockId: null,
  docId: null,
  error: null,
  existingAnnotations: [],
  initialText: '',

  openEditor: async (docId, blockId, initialText = '') => {
    set({ editingBlockId: blockId, docId, error: null, initialText });
    // 8.6 打开编辑器时加载已有批注列表
    try {
      const annotations = await annotationRepository.listAnnotations(docId, blockId);
      set({ existingAnnotations: annotations });
    } catch (e) {
      set({ existingAnnotations: [], error: (e as Error).message });
    }
  },
  closeEditor: () =>
    set({ editingBlockId: null, docId: null, existingAnnotations: [], initialText: '' }),
  saveAnnotation: async (text) => {
    const { docId, editingBlockId } = get();
    if (!docId || !editingBlockId || !text.trim()) return;
    try {
      // 1. 写入 block 的 annotations（原有逻辑）
      await annotationRepository.addAnnotation(docId, editingBlockId, text.trim());

      // 2. 同步到知识点系统（驼峰中可见）
      const docTitle = useReaderStore.getState().docIndex?.meta.title;
      // 取 block 原文作为上下文
      const blocks = useReaderStore.getState().blocks;
      const block = blocks.find((b) => b.id === editingBlockId);
      await knowledgeRepository.addNote(
        text.trim(),
        docId,
        editingBlockId,
        docTitle,
        block?.original
      );

      // 8.6 刷新已有批注列表
      const annotations = await annotationRepository.listAnnotations(docId, editingBlockId);
      set({ existingAnnotations: annotations });
    } catch (e) {
      set({ error: (e as Error).message });
    }
  },
  // 8.6 编辑已有批注
  updateAnnotation: async (annotationId, text) => {
    const { docId, editingBlockId } = get();
    if (!docId || !editingBlockId || !text.trim()) return;
    try {
      await annotationRepository.updateAnnotation(
        docId,
        editingBlockId,
        annotationId,
        text.trim()
      );
      const annotations = await annotationRepository.listAnnotations(docId, editingBlockId);
      set({ existingAnnotations: annotations });
    } catch (e) {
      set({ error: (e as Error).message });
    }
  },
  // 8.6 删除已有批注
  removeAnnotation: async (annotationId) => {
    const { docId, editingBlockId } = get();
    if (!docId || !editingBlockId) return;
    try {
      await annotationRepository.removeAnnotation(docId, editingBlockId, annotationId);
      const annotations = await annotationRepository.listAnnotations(docId, editingBlockId);
      set({ existingAnnotations: annotations });
    } catch (e) {
      set({ error: (e as Error).message });
    }
  },
}));
